import { memoize } from 'lodash'
import { APIConfig } from 'types/definitions'
import { InventoryItemServicePromiseClient } from '@taxfyle/api-internal/internal/inventory_item_grpc_web_pb'
import { enableDevTools } from 'utils/grpc-util'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import {
  CreateInventoryItemRequest,
  ArchiveInventoryItemRequest,
  ListInventoryItemsRequest,
  ListWorkspacesWithInventoryItemsRequest,
  ListInventoryItemsForProvidersRequest,
  SetProVisibilityRequest,
  UnarchiveInventoryItemRequest,
  GetManyInventoryItemsRequest,
} from '@taxfyle/api-internal/internal/inventory_item_pb'

/**
 * Creates an instance of the Inventory API.
 *
 * @param config
 */
export function createInventoryAPI(config: APIConfig) {
  return new InventoryAPI(config)
}

export class InventoryAPI {
  private readonly inventoryGrpcClient = createJobGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async listInventoryItems(request: ListInventoryItemsRequest) {
    return this.inventoryGrpcClient.listInventoryItems(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listInventoryItemsForProviders(
    request: ListInventoryItemsForProvidersRequest,
  ) {
    return this.inventoryGrpcClient.listInventoryItemsForProviders(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async createInventoryItem(request: CreateInventoryItemRequest) {
    return this.inventoryGrpcClient.createInventoryItem(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async archiveInventoryItem(request: ArchiveInventoryItemRequest) {
    return this.inventoryGrpcClient.archiveInventoryItem(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async unarchiveInventoryItem(request: UnarchiveInventoryItemRequest) {
    return this.inventoryGrpcClient.unarchiveInventoryItem(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async updateInventoryItem(request: any) {
    return this.inventoryGrpcClient.updateInventoryItem(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listWorkspacesWithInventoryItems(
    request: ListWorkspacesWithInventoryItemsRequest,
  ) {
    return this.inventoryGrpcClient.listWorkspacesWithInventoryItems(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async setProVisibilityForInventoryItem(request: SetProVisibilityRequest) {
    return this.inventoryGrpcClient.setProVisibility(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async getManyInventoryItems(request: GetManyInventoryItemsRequest) {
    return this.inventoryGrpcClient.getManyInventoryItems(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * Creates a work dotnet client for inventory items service that is memoized.
 */
const createJobGrpcClient = memoize(function createJobGrpcClient(
  baseUrl: string,
) {
  const client = new InventoryItemServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})
